import React from 'react';
import { Box, Button, Chip, Container, Divider, Typography } from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import ArrowBackRounded from '@material-ui/icons/ArrowBackRounded';
import CheckCircleRounded from '@material-ui/icons/CheckCircleRounded';
import NewReleasesRounded from '@material-ui/icons/NewReleasesRounded';
import TuneRounded from '@material-ui/icons/TuneRounded';
import BuildRounded from '@material-ui/icons/BuildRounded';
import config from '@/config';
import { releaseNotes, ReleaseNoteCategory } from './releaseNotes';

const categoryIcon: Record<ReleaseNoteCategory, React.ReactNode> = {
  New: <NewReleasesRounded fontSize="small" />,
  Improved: <TuneRounded fontSize="small" />,
  Fixed: <BuildRounded fontSize="small" />,
};

const ReleaseNotesPage = ({ history }: { history?: { goBack: () => void } }) => {
  const classes = useStyles();
  const latest = releaseNotes[0];
  const build = config.appVersion ? config.appVersion.substring(0, 8) : 'Development';

  return (
    <Box className={classes.root}>
      <Container maxWidth="md" className={classes.container}>
        <Box className={classes.nav}>
          <Button startIcon={<ArrowBackRounded />} onClick={() => history?.goBack()} className={classes.backButton}>
            Back
          </Button>
          <Chip label={`Build ${build}`} className={classes.buildChip} />
        </Box>

        <Box className={classes.header}>
          <Typography variant="h1" className={classes.title}>
            Release Notes
          </Typography>
          <Typography className={classes.subtitle}>
            Product updates for the operational screens, schedules, tablets, and admin tools your teams use each day.
          </Typography>
        </Box>

        <Box className={classes.latestStrip}>
          <Box>
            <Typography className={classes.latestLabel}>Latest</Typography>
            <Typography className={classes.latestTitle}>Version {latest.version}</Typography>
            <Typography className={classes.latestSummary}>{latest.headline}</Typography>
          </Box>
          <Box className={classes.highlightList}>
            {latest.highlights.map(highlight => (
              <Box className={classes.highlight} key={highlight}>
                <CheckCircleRounded fontSize="small" />
                <span>{highlight}</span>
              </Box>
            ))}
          </Box>
        </Box>

        <Box className={classes.timeline}>
          {releaseNotes.map((release, index) => (
            <Box className={classes.release} key={release.version}>
              <Box className={classes.releaseMeta}>
                <Typography className={classes.releaseDate}>{release.date}</Typography>
                <Typography className={classes.releaseVersion}>{release.version}</Typography>
              </Box>
              <Box className={classes.releaseBody}>
                <Box className={classes.releaseTitleRow}>
                  <Typography variant="h2" className={classes.releaseTitle}>
                    {release.headline}
                  </Typography>
                  {index === 0 && <Chip label="Latest" className={classes.latestChip} />}
                </Box>
                <Typography className={classes.releaseSummary}>{release.summary}</Typography>

                {release.demoUrl && (
                  <Box
                    className={classes.demoWrapper}
                    style={{ paddingBottom: `calc(${release.demoAspectRatio ?? 56.25}% + 25px)` }}
                  >
                    <iframe
                      loading="lazy"
                      title={`Version ${release.version} demo`}
                      src={release.demoUrl}
                      allow="fullscreen"
                      allowFullScreen
                      className={classes.demoFrame}
                    />
                  </Box>
                )}

                <Box className={classes.items}>
                  {release.items.map(item => (
                    <Box className={classes.item} key={`${release.version}-${item.title}`}>
                      <Box className={classes.itemIcon}>{categoryIcon[item.category]}</Box>
                      <Box>
                        <Typography className={classes.itemLabel}>{item.category}</Typography>
                        <Typography className={classes.itemTitle}>{item.title}</Typography>
                        {item.audience && (
                          <Typography className={classes.itemAudience}>User(s): {item.audience}</Typography>
                        )}
                        <Typography className={classes.itemDescription}>{item.description}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
              {index !== releaseNotes.length - 1 && <Divider className={classes.releaseDivider} />}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    background: '#000C3F',
    color: '#E6ECEF',
    overflowY: 'auto',
  },
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(8),
  },
  nav: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(6),
  },
  backButton: {
    color: alpha('#E6ECEF', 0.72),
    textTransform: 'none',
    '&:hover': {
      background: alpha('#E6ECEF', 0.06),
    },
  },
  buildChip: {
    background: alpha('#1a2553', 0.72),
    color: alpha('#E6ECEF', 0.72),
    fontWeight: 500,
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  eyebrow: {
    color: '#00A7F7',
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: 0,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
  },
  title: {
    color: '#fff',
    fontSize: '3rem',
    fontWeight: 500,
    letterSpacing: 0,
    lineHeight: 1.08,
    marginBottom: theme.spacing(1.5),
  },
  subtitle: {
    color: alpha('#E6ECEF', 0.72),
    fontSize: '1.05rem',
    lineHeight: 1.65,
    maxWidth: 640,
  },
  latestStrip: {
    alignItems: 'flex-start',
    background: alpha('#17214C', 0.72),
    border: `1px solid ${alpha('#E6ECEF', 0.1)}`,
    borderRadius: 8,
    display: 'grid',
    gap: theme.spacing(3),
    gridTemplateColumns: 'minmax(0, 1fr) 17rem',
    marginBottom: theme.spacing(6),
    padding: theme.spacing(2.5),
  },
  latestLabel: {
    color: alpha('#E6ECEF', 0.48),
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  latestTitle: {
    color: '#fff',
    fontSize: '1.15rem',
    fontWeight: 500,
    marginTop: theme.spacing(0.5),
  },
  latestSummary: {
    color: alpha('#E6ECEF', 0.66),
    lineHeight: 1.5,
    marginTop: theme.spacing(0.75),
  },
  highlightList: {
    display: 'grid',
    gap: theme.spacing(0.75),
  },
  highlight: {
    alignItems: 'center',
    color: alpha('#E6ECEF', 0.76),
    display: 'flex',
    fontSize: '0.9rem',
    gap: theme.spacing(1),
    lineHeight: 1.35,
    '& svg': {
      color: '#00A7F7',
      flex: '0 0 auto',
    },
  },
  timeline: {
    display: 'grid',
    gap: theme.spacing(4),
  },
  release: {
    position: 'relative',
  },
  releaseMeta: {
    marginBottom: theme.spacing(1.5),
  },
  releaseDate: {
    color: alpha('#E6ECEF', 0.44),
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  releaseVersion: {
    color: '#00A7F7',
    fontSize: '1rem',
    fontWeight: 700,
    marginTop: theme.spacing(0.25),
  },
  releaseBody: {
    minWidth: 0,
  },
  releaseTitleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
    justifyContent: 'space-between',
  },
  releaseTitle: {
    color: '#fff',
    fontSize: '1.45rem',
    fontWeight: 500,
    letterSpacing: 0,
    lineHeight: 1.25,
  },
  latestChip: {
    background: alpha('#00A7F7', 0.16),
    color: '#7dd3fc',
    fontWeight: 700,
  },
  releaseSummary: {
    color: alpha('#E6ECEF', 0.62),
    lineHeight: 1.6,
    marginTop: theme.spacing(1),
    maxWidth: 720,
  },
  demoWrapper: {
    height: 0,
    marginTop: theme.spacing(3),
    position: 'relative',
    width: '100%',
  },
  demoFrame: {
    border: `1px solid ${alpha('#3f5fac', 0.35)}`,
    borderRadius: 10,
    boxShadow: '0px 0px 18px rgba(26, 19, 72, 0.15)',
    boxSizing: 'border-box',
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  items: {
    display: 'grid',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2.5),
  },
  item: {
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: '1.75rem minmax(0, 1fr)',
  },
  itemIcon: {
    alignItems: 'center',
    color: '#00A7F7',
    display: 'flex',
    height: '1.75rem',
    justifyContent: 'center',
    width: '1.75rem',
    '& svg': {
      fontSize: '1.1rem',
    },
  },
  itemLabel: {
    color: alpha('#E6ECEF', 0.42),
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  itemTitle: {
    color: '#E6ECEF',
    fontSize: '0.98rem',
    fontWeight: 500,
    marginTop: theme.spacing(0.25),
  },
  itemAudience: {
    color: alpha('#E6ECEF', 0.48),
    fontSize: '0.82rem',
    lineHeight: 1.45,
    marginTop: theme.spacing(0.35),
  },
  itemDescription: {
    color: alpha('#E6ECEF', 0.6),
    lineHeight: 1.55,
    marginTop: theme.spacing(0.35),
  },
  releaseDivider: {
    background: alpha('#E6ECEF', 0.1),
    marginTop: theme.spacing(4),
  },
  [theme.breakpoints.down('xs')]: {
    container: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    nav: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(4),
    },
    title: {
      fontSize: '2.25rem',
    },
    latestStrip: {
      gridTemplateColumns: '1fr',
      marginBottom: theme.spacing(4),
    },
    releaseTitleRow: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
}));

export default ReleaseNotesPage;
